import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CartContext = createContext(null);
const STORAGE_KEY = "asg-cart-v2";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return (JSON.parse(localStorage.getItem(STORAGE_KEY)) || []).filter((i) => !String(i.id).startsWith("framing-"));
    } catch {
      return [];
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = useCallback((card) => {
    setItems((prev) => {
      const found = prev.find((i) => i.id === card.id);
      if (found) return prev.map((i) => (i.id === card.id ? { ...i, qty: Math.min(i.qty + 1, 20) } : i));
      return [...prev, { id: card.id, title: card.title, price_cents: card.price_cents, image: card.image, lookup_key: card.lookup_key, qty: 1 }];
    });
    toast.success("Added to your portfolio bag", { description: card.title });
  }, []);

  const setQty = useCallback((id, qty) => {
    setItems((prev) => (qty <= 0 ? prev.filter((i) => i.id !== id) : prev.map((i) => (i.id === id ? { ...i, qty } : i))));
  }, []);

  const remove = useCallback((id) => setItems((prev) => prev.filter((i) => i.id !== id)), []);
  const clear = useCallback(() => setItems([]), []);

  const { subtotal, count } = useMemo(() => {
    return {
      subtotal: items.reduce((s, i) => s + i.price_cents * i.qty, 0),
      count: items.reduce((s, i) => s + i.qty, 0),
    };
  }, [items]);

  const checkout = useCallback(async () => {
    if (!items.length) return;
    setCheckingOut(true);
    try {
      const { data } = await API.post("/payments/checkout", {
        items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.qty })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e, "Checkout could not be started."));
      setCheckingOut(false);
    }
  }, [items]);

  const value = { items, add, setQty, remove, clear, subtotal, count, drawerOpen, setDrawerOpen, checkout, checkingOut };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
